import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, ChevronLeft, ChevronRight, ChevronRight as Arrow, Eye } from 'lucide-react';
import API from '../../lib/api';
import AdminFilterBar from './AdminFilterBar';

const SINCE_OPTIONS = [
  { value: '',    label: 'All time' },
  { value: 24,    label: 'Last 24 hours' },
  { value: 168,   label: 'Last 7 days' },
  { value: 720,   label: 'Last 30 days' },
  { value: 2160,  label: 'Last 90 days' },
];

function formatRelative(dateStr) {
  if (!dateStr) return '–';
  const d = new Date(dateStr);
  const diffMs = Date.now() - d.getTime();
  if (diffMs < 0) return d.toLocaleString('en-IN');
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString('en-IN');
}

export default function CustomerLogsList() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({ search: '', since: '' });
  const [page, setPage]       = useState(1);
  const [data, setData]       = useState({ customers: [], total: 0, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { page, limit: 25 };
      if (filters.search)            params.search = filters.search;
      if (filters.since !== '')      params.since  = filters.since;
      const { data: res } = await API.get('/admin/customer-logs', { params });
      setData({
        customers: res.customers || [],
        total:     res.total || 0,
        pages:     res.pages || 1,
      });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load customer logs.');
    }
    setLoading(false);
  }, [page, filters.search, filters.since]);

  useEffect(() => { load(); }, [load]);

  const handleFilter = (key, val) => {
    setFilters((f) => ({ ...f, [key]: val }));
    setPage(1);
  };
  const handleReset = () => { setFilters({ search: '', since: '' }); setPage(1); };

  return (
    <div>
      <div className="mb-5">
        <h1 className="text-2xl font-heading font-bold text-[#1A1A2E] flex items-center gap-2">
          <Activity className="h-5 w-5 text-[#6C63FF]" /> Customer Logs
        </h1>
        <p className="text-sm text-[#6B7280] font-body mt-1">
          Per-customer activity audit — every authenticated request a customer makes is recorded
          with action, IP address, and user-agent. Click a customer to drill in.
        </p>
      </div>

      <AdminFilterBar filters={filters} onFilterChange={handleFilter} onReset={handleReset}>
        <select
          value={filters.since}
          onChange={(e) => handleFilter('since', e.target.value === '' ? '' : Number(e.target.value))}
          className="text-sm font-body border border-[#E8E6F8] rounded-lg px-3 py-2 bg-[#F5F4FB] text-[#1A1A2E] focus:outline-none focus:ring-1 focus:ring-[#6C63FF]/50"
        >
          {SINCE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </AdminFilterBar>

      <div className="bg-white rounded-2xl border border-[#E8E6F8] shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-6 h-6 border-2 border-[#6C63FF] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="p-6 text-center">
            <p className="text-sm text-red-500 font-body">{error}</p>
            <button onClick={load} className="mt-2 text-xs text-[#6C63FF] underline font-body">Retry</button>
          </div>
        ) : data.customers.length === 0 ? (
          <p className="text-center text-[#9CA3AF] text-sm font-body py-12">
            No customer activity for this filter.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm font-body">
              <thead>
                <tr className="bg-[#F5F4FB] border-b border-[#E8E6F8]">
                  {['Customer', 'Phone', 'Events', 'Unique IPs', 'Last Action', 'Last IP', 'Last Seen', ''].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-heading font-semibold text-[#6B7280] whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E8E6F8]">
                {data.customers.map((c) => (
                  <tr
                    key={c.id}
                    onClick={() => navigate(`/admin/customer-logs/${c.id}`)}
                    className="hover:bg-[#F5F4FB] transition-colors cursor-pointer"
                  >
                    <td className="px-4 py-3">
                      <p className="font-medium text-[#1A1A2E]">{c.name || '–'}</p>
                      <p className="text-xs text-[#9CA3AF]">{c.email || `User #${c.id}`}</p>
                    </td>
                    <td className="px-4 py-3 text-[#6B7280]">{c.phone || '–'}</td>
                    <td className="px-4 py-3 text-[#1A1A2E] font-medium">{c.event_count}</td>
                    <td className="px-4 py-3 text-[#6B7280]">{c.unique_ip_count}</td>
                    <td className="px-4 py-3 text-xs">
                      <span className="inline-block bg-[#6C63FF]/10 text-[#6C63FF] px-2 py-0.5 rounded font-mono">
                        {c.last_action || '–'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-[#6B7280] font-mono">{c.last_ip || '–'}</td>
                    <td className="px-4 py-3 text-xs text-[#9CA3AF] whitespace-nowrap" title={c.last_activity_at ? new Date(c.last_activity_at).toLocaleString('en-IN') : ''}>
                      {formatRelative(c.last_activity_at)}
                    </td>
                    <td className="px-4 py-3">
                      <Eye className="h-3.5 w-3.5 text-[#6C63FF]" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {data.pages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-xs text-[#6B7280] font-body">
            Showing {(page - 1) * 25 + 1}–{Math.min(page * 25, data.total)} of {data.total} customers
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="flex items-center gap-1 text-xs font-body px-3 py-1.5 border border-[#E8E6F8] rounded-lg hover:bg-[#F5F4FB] disabled:opacity-40"
            >
              <ChevronLeft className="h-3.5 w-3.5" /> Prev
            </button>
            <span className="text-xs font-body text-[#6B7280]">Page {page} / {data.pages}</span>
            <button
              onClick={() => setPage((p) => Math.min(data.pages, p + 1))}
              disabled={page === data.pages}
              className="flex items-center gap-1 text-xs font-body px-3 py-1.5 border border-[#E8E6F8] rounded-lg hover:bg-[#F5F4FB] disabled:opacity-40"
            >
              Next <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
